import { setup, assign } from "xstate";
import type { models } from "jiffy-api";
import type { Artwork } from "@design-lab/shared/types/artwork";

export type EnhancementSource = "upload" | "design_library" | "canva" | "ai_generation";

type EnhancementState = "idle" | "progress" | "uploaded" | "enhancing" | "warming" | "done";

export type EnhancementActorContext = {
  file?: File;
  artworkId?: number;
  artwork?: Artwork;
  source?: EnhancementSource;
  cacheWarmingStatus: "started" | "warmed" | "none";
  visitedStates: EnhancementState[];
};

export type EnhancementActorInput = {
  file?: File;
  artworkId?: number;
  source?: EnhancementSource;
};

export type EnhancementActorOutput = {
  artwork: Artwork;
  artworkId: number;
};

export const enhancementActor = setup({
  types: {
    context: {} as EnhancementActorContext,
    input: {} as EnhancementActorInput,
    output: {} as EnhancementActorOutput,
    events: {} as
      | { type: "upload.started"; file: File; source: EnhancementSource }
      | { type: "upload.completed"; id: number }
      | { type: "enhancement.started" }
      | { type: "enhancement.completed"; artwork: Artwork }
      | { type: "cache.warming.started" }
      | { type: "cache.warmed" }
      | { type: "websocket.event"; name: models.cable.DtfChannel.Actions["action"] }
  }
}).createMachine({
  id: "enhancement",
  context: ({ input }) => {
    const ctx: EnhancementActorContext = {
      cacheWarmingStatus: "none",
      visitedStates: []
    };
    if (input.file !== undefined) ctx.file = input.file;
    if (input.artworkId !== undefined) ctx.artworkId = input.artworkId;
    if (input.source !== undefined) ctx.source = input.source;
    return ctx;
  },
  initial: "idle",
  on: {
    "websocket.event": {}
  },
  states: {
    idle: {
      on: {
        "upload.started": {
          target: "progress",
          actions: assign({
            file: ({ event }) => (event as any).file,
            source: ({ event }) => (event as any).source
          })
        },
        "enhancement.started": {
          target: "enhancing"
        }
      }
    },
    progress: {
      entry: assign({ visitedStates: ({ context }) => [...context.visitedStates, "progress"] }),
      on: {
        "upload.completed": {
          target: "uploaded",
          actions: assign({ artworkId: ({ event }) => (event as any).id })
        }
      }
    },
    uploaded: {
      entry: assign({ visitedStates: ({ context }) => [...context.visitedStates, "uploaded"] }),
      on: {
        "enhancement.started": { target: "enhancing" },
        "enhancement.completed": {
          target: "done",
          actions: assign({ artwork: ({ event }) => (event as any).artwork })
        }
      }
    },
    enhancing: {
      entry: assign({ visitedStates: ({ context }) => [...context.visitedStates, "enhancing"] }),
      on: {
        "cache.warming.started": {
          target: "warming",
          actions: assign({ cacheWarmingStatus: "started" })
        },
        "enhancement.completed": {
          target: "done",
          actions: assign({ artwork: ({ event }) => (event as any).artwork })
        }
      }
    },
    warming: {
      entry: assign({ visitedStates: ({ context }) => [...context.visitedStates, "warming"] }),
      on: {
        "cache.warmed": {
          actions: assign({ cacheWarmingStatus: "warmed" })
        },
        "enhancement.completed": {
          target: "done",
          actions: assign({ artwork: ({ event }) => (event as any).artwork })
        }
      }
    },
    done: {
      type: "final",
      entry: assign({ visitedStates: ({ context }) => [...context.visitedStates, "done"] }),
      output: ({ context }) => ({
        artwork: context.artwork!,
        artworkId: context.artworkId!
      })
    }
  }
});
